import dayjs from 'dayjs';
import { useEffect, useState, useRef, useMemo } from 'react';
import { Button, FormControl } from 'react-bootstrap';
import webSocket from '../../webSocket';
import { list } from '../../api/message';
import { subscribeToMessages } from './messageBus';
import './chatWindow.css';
import { DATETIME_FORMAT_WITHOUT_SECOND } from '../../shared/constants';

/**
 * ChatWindow component displays the chat interface for a specific user.
 * @param {Object} currentUser - The user currently logged in. Object should contains `id` & `fullName` property.
 * @param {Object} chatWith - The user to chat with. Object should contains `id` & `fullName` property.
 * @returns {JSX.Element} The chat window component.
 * @description This component handles displaying messages, sending new messages, and subscribing to real-time message
 */
const ChatWindow = ({ currentUser, chatWith }) => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(500);
  const pagination = useMemo(() => ({
    page,
    pageSize
  }), [page, pageSize]);
  const messagesEndRef = useRef(null);

  const receiver = chatWith?.id;

  useEffect(() => {
    const unsubscribe = subscribeToMessages((data) => {
      if ([receiver, currentUser.id].includes(data.sender) && [receiver, currentUser.id].includes(data.receiver)) {
        setMessages(prevMessages => [
          ...prevMessages, data,
        ]);

        if (data.sender !== currentUser.id) {
          webSocket.send('readMessage', {
            sender: receiver,
          });
        }
      }
    });

    return () => unsubscribe(); // Clean up on unmount
  });

  useEffect(() => {
    if (receiver) {
      getMessages(receiver, pagination);
    }
  }, [receiver, pagination]);

  const getMessages = async (receiver, pagination) => {
    if (!receiver) return;
    try {
      const res = await list({ receiverId: receiver, pagination });
      const sortedMessages = res.items?.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)) || [];

      if (pagination.page === 1) {
        setMessages(sortedMessages ?? []);
      } else {
        setMessages(prevMessages => [...prevMessages, ...(sortedMessages ?? [])]);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }

  const sendMessage = () => {
    if (!input.trim()) return;
    if (webSocket.socket && webSocket.socket.connected) {
      webSocket.send('sendMessage', {
        sender: currentUser.id,
        senderFullName: currentUser.fullName,
        receiver: receiver,
        receiverFullName: chatWith.fullName,
        message: input.trim(),
      });
    }
    setInput('');
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
      inline: 'nearest'
    });
  });

  return !receiver ? (<></>) : (
    <div style={{ width: '75%', padding: '20px', height: 'calc(100vh - 60px)', display: 'flex', flexDirection: 'column' }}>
      <h3>{chatWith.fullName}</h3>
      <hr style={{ margin: 0 }}></hr>
      <div style={{ flex: 1, overflowY: 'auto', marginBottom: '10px' }}>
        {messages.map((msg, index) => (
          <div className={`message ${msg.sender === currentUser.id ? 'sent' : 'received'}`} key={index}>
            <span>{msg.message}</span>
            <span className="timestamp">{dayjs(msg.createdAt).format(DATETIME_FORMAT_WITHOUT_SECOND)}</span>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      <hr></hr>
      <div style={{ display: 'flex', alignItems: 'center', alignContent: 'center' }}>
        <FormControl
          id='message'
          type='text'
          placeholder='Type a message'
          as="textarea"
          min={1}
          max={3}
          style={{ width: '100%' }}
          value={input}
          onChange={(e) => setInput(e.target.value)}
        />
        <Button variant="primary" style={{ marginLeft: '10px' }} onClick={sendMessage}>Send</Button>
      </div>
    </div>
  );
};

export default ChatWindow;
